/**
 * AVTALEN MELLOM BYRÅET OG ARTISTEN — `/v1/artists/:id/agreement` (ARTIST-AVTALE-ORDREN Del 1),
 * sperreperiodene, og byråets egne notater (Del 2).
 *
 * =================================================================================================
 * ALT ER VALGFRITT, OG DET ER SELVE KONTRAKTEN.
 *
 * Atle: «Avtalen skal bare si hva som er avtalt — ikke regne.» Et skjema som krevde felt ville
 * tvunget fram tall ingen har avtalt, og et oppdiktet tall i en avtale ser ut som noe man kan
 * handle på. Derfor er hvert felt `nullable` i DTO-en og `nullish` i oppdateringen: fraværet er en
 * gyldig tilstand, ikke en ufullstendig en.
 *
 * `null` og «ikke sendt» betyr to forskjellige ting i oppdateringen — det første tømmer feltet, det
 * andre lar det stå. Det er den samme formen `updateBookingAgencyRequest` har.
 *
 * =================================================================================================
 * BASISPUNKTER, IKKE PROSENT.
 *
 * 12,5 % er 1250. Ordren skriver «%» fordi det er det mennesket ser, men huset har regnet i
 * basispunkter siden 0165, og en kontrakt som byttet enhet ville flyttet avrundingen til
 * grensesnittet. Null er en ekte sats — et byrå kan ha en artist de ikke tar provisjon av — og
 * fraværet er `null`.
 */
import { z } from 'zod';
import { wireInstant } from './wire-time.js';

/** Fritekstfelt: lange nok til et avsnitt, korte nok til at ingen limer inn en hel kontrakt. */
const MAX_AGREEMENT_TEXT = 2000;
/** «Firmafest», «Politisk», «Sponsor». Chips, ikke essays. */
const MAX_EXCLUDED_WORK = 40;
export const MAX_EXCLUDED_WORK_ITEMS = 20;

export const agreementRoles = ['booking', 'management', 'both'] as const;
export const signingAuthorities = ['agency_signs', 'artist_approves'] as const;
export const commissionBases = ['gross', 'net_of_costs'] as const;
export const invoicingParties = ['agency', 'artist'] as const;
export const travelExpenseTerms = ['in_fee', 'billed_separately'] as const;

export type AgreementRole = (typeof agreementRoles)[number];
export type SigningAuthority = (typeof signingAuthorities)[number];
export type CommissionBasis = (typeof commissionBases)[number];
export type InvoicingParty = (typeof invoicingParties)[number];
export type TravelExpenseTerm = (typeof travelExpenseTerms)[number];

export const artistAgreementDtoSchema = z
  .object({
    id: z.uuid(),
    bookingArtistId: z.uuid(),

    /* --- omfang --- */
    role: z.enum(agreementRoles).nullable(),
    territory: z.string().nullable(),
    territoryExceptions: z.string().nullable(),
    exclusive: z.boolean().nullable(),
    signingAuthority: z.enum(signingAuthorities).nullable(),

    /* --- provisjon og oppgjør --- */
    commissionBookingBp: z.number().int().min(0).max(10000).nullable(),
    commissionManagementBp: z.number().int().min(0).max(10000).nullable(),
    commissionBasis: z.enum(commissionBases).nullable(),
    invoicesOrganiser: z.enum(invoicingParties).nullable(),
    settlementDays: z.number().int().min(0).max(365).nullable(),
    travelExpenses: z.enum(travelExpenseTerms).nullable(),
    commissionOnExpenses: z.boolean().nullable(),

    /* --- varighet. Tom sluttdato er «løpende». --- */
    startsOn: z.iso.date().nullable(),
    endsOn: z.iso.date().nullable(),
    noticeMonths: z.number().int().min(0).max(120).nullable(),
    sunsetMonths: z.number().int().min(0).max(120).nullable(),

    /* --- rammer --- */
    minimumFeeMinor: z.number().int().min(0).nullable(),
    excludedWork: z.array(z.string()),
    exclusivityRadiusKm: z.number().int().min(0).max(20000).nullable(),
    exclusivityDays: z.number().int().min(0).max(3650).nullable(),

    /* --- dokument og særvilkår. Filen ligger i dokumentmappa «Avtale». --- */
    signedBy: z.string().nullable(),
    signedOn: z.iso.date().nullable(),
    specialTerms: z.string().nullable(),

    /** Revisjonen, som overalt ellers: hvem rørte den sist, og når. */
    updatedAt: wireInstant,
    updatedByName: z.string().nullable(),
  })
  .meta({ id: 'ArtistAgreementDto' });
export type ArtistAgreementDto = z.infer<typeof artistAgreementDtoSchema>;

/**
 * PATCH, felt for felt.
 *
 * Ingen `create`: avtalen er én rad per artist, og den finnes i det noen skriver i den. Tjenesten
 * gjør en upsert — et skjema som krevde at man «opprettet avtalen» først ville vært et klikk som
 * ikke betyr noe.
 */
export const updateArtistAgreementRequestSchema = z
  .object({
    role: z.enum(agreementRoles).nullish(),
    territory: z.string().trim().min(1).max(200).nullish(),
    territoryExceptions: z.string().trim().min(1).max(MAX_AGREEMENT_TEXT).nullish(),
    exclusive: z.boolean().nullish(),
    signingAuthority: z.enum(signingAuthorities).nullish(),

    commissionBookingBp: z.number().int().min(0).max(10000).nullish(),
    commissionManagementBp: z.number().int().min(0).max(10000).nullish(),
    commissionBasis: z.enum(commissionBases).nullish(),
    invoicesOrganiser: z.enum(invoicingParties).nullish(),
    settlementDays: z.number().int().min(0).max(365).nullish(),
    travelExpenses: z.enum(travelExpenseTerms).nullish(),
    commissionOnExpenses: z.boolean().nullish(),

    startsOn: z.iso.date().nullish(),
    endsOn: z.iso.date().nullish(),
    noticeMonths: z.number().int().min(0).max(120).nullish(),
    sunsetMonths: z.number().int().min(0).max(120).nullish(),

    minimumFeeMinor: z.number().int().min(0).nullish(),
    excludedWork: z
      .array(z.string().trim().min(1).max(MAX_EXCLUDED_WORK))
      .max(MAX_EXCLUDED_WORK_ITEMS)
      .nullish(),
    exclusivityRadiusKm: z.number().int().min(0).max(20000).nullish(),
    exclusivityDays: z.number().int().min(0).max(3650).nullish(),

    signedBy: z.string().trim().min(1).max(200).nullish(),
    signedOn: z.iso.date().nullish(),
    specialTerms: z.string().trim().min(1).max(MAX_AGREEMENT_TEXT).nullish(),
  })
  .meta({ id: 'UpdateArtistAgreementRequest' });
export type UpdateArtistAgreementRequest = z.infer<typeof updateArtistAgreementRequestSchema>;

/* ------------------------------------------------------------------ sperreperioder */

/**
 * NÅR ARTISTEN IKKE KAN BOOKES.
 *
 * Datoene er inklusive i begge ender: en sperre på én dag har den samme datoen to ganger. Det er
 * slik et menneske skriver «1.–31. mars», og det er slik kalenderen tegner den.
 */
export const artistBlackoutDtoSchema = z
  .object({
    id: z.uuid(),
    bookingArtistId: z.uuid(),
    fromDate: z.iso.date(),
    toDate: z.iso.date(),
    note: z.string().nullable(),
  })
  .meta({ id: 'ArtistBlackoutDto' });
export type ArtistBlackoutDto = z.infer<typeof artistBlackoutDtoSchema>;

export const artistBlackoutListResponseSchema = z
  .object({ items: z.array(artistBlackoutDtoSchema) })
  .meta({ id: 'ArtistBlackoutListResponse' });
export type ArtistBlackoutListResponse = z.infer<typeof artistBlackoutListResponseSchema>;

export const createArtistBlackoutRequestSchema = z
  .object({
    fromDate: z.iso.date(),
    toDate: z.iso.date(),
    note: z.string().trim().min(1).max(400).nullish(),
  })
  .refine((one) => one.toDate >= one.fromDate, {
    message: 'toDate must not be before fromDate',
    path: ['toDate'],
  })
  .meta({ id: 'CreateArtistBlackoutRequest' });
export type CreateArtistBlackoutRequest = z.infer<typeof createArtistBlackoutRequestSchema>;

/* ------------------------------------------------------------------ notater */

/**
 * BYRÅETS EGNE NOTATER OM ARTISTEN (Del 2).
 *
 * En LISTE med avsender og tid, ikke ett felt alle overskriver. En agent som leser «vanskelig å få
 * tak i på fredager» trenger å vite om det ble skrevet i fjor eller i går, og av hvem.
 */
export const artistNoteDtoSchema = z
  .object({
    id: z.uuid(),
    bookingArtistId: z.uuid(),
    body: z.string(),
    createdAt: wireInstant,
    /** Navnet, oppslått — lista skal ikke måtte slå opp i kontaktregisteret selv. */
    createdByName: z.string().nullable(),
  })
  .meta({ id: 'ArtistNoteDto' });
export type ArtistNoteDto = z.infer<typeof artistNoteDtoSchema>;

export const artistNoteListResponseSchema = z
  .object({ items: z.array(artistNoteDtoSchema) })
  .meta({ id: 'ArtistNoteListResponse' });
export type ArtistNoteListResponse = z.infer<typeof artistNoteListResponseSchema>;

export const createArtistNoteRequestSchema = z
  .object({ body: z.string().trim().min(1).max(4000) })
  .meta({ id: 'CreateArtistNoteRequest' });
export type CreateArtistNoteRequest = z.infer<typeof createArtistNoteRequestSchema>;

/* ------------------------------------------------------------------ hele avtalebildet */

/**
 * AVTALEN OG SPERREPERIODENE I ETT SVAR — det `GET /v1/artists/:id/agreement` gir.
 *
 * =================================================================================================
 * `agreement` ER NULLBAR, OG DET ER FORSKJELLEN PÅ «INGEN AVTALE» OG «EN TOM AVTALE».
 *
 * Raden finnes først når noen har skrevet i den (se PATCH-en over: ingen `create`). Å la GET-en
 * opprette den ville gjort en LESNING til en skriving — en revisjonsrad og en `updated_by` for at
 * noen åpnet en fane. Skjermen tegner den tomme tilstanden fra `null`, og den første tastetrykket
 * er det som lager raden.
 *
 * =================================================================================================
 * TRE FLATER LESER DETTE SVARET, OG DET ER GRUNNEN TIL AT DET ER ETT SVAR.
 *
 * Avtale-fanen tegner feltene. Kalender-fanen tegner sperreperiodene som utilgjengelige dager.
 * Forespørselsmodalen spør om datoen ligger i en av dem, og om honoraret er under minstehonoraret
 * — som står i avtalen. To spørringer ville gitt to øyeblikk, og en agent som nettopp la inn en
 * sperre ville sett den i kalenderen og ikke i advarselen.
 */
export const artistAgreementViewSchema = z
  .object({
    agreement: artistAgreementDtoSchema.nullable(),
    blackouts: z.array(artistBlackoutDtoSchema),
  })
  .meta({ id: 'ArtistAgreementView' });
export type ArtistAgreementView = z.infer<typeof artistAgreementViewSchema>;

/**
 * LIGGER DATOEN I EN SPERREPERIODE? Ett sted, fordi tre flater spør.
 *
 * Datoene er inklusive i begge ender (se `artistBlackoutDtoSchema`), og sammenlikningen er på
 * TEKST. `YYYY-MM-DD` sorterer leksikografisk likt som kronologisk, og en `Date` ville dratt inn
 * en tidssone på et felt som ikke har en — den samme fellen `daysBetween` unngår på skjermen.
 */
export function blackoutOn<T extends { fromDate: string; toDate: string }>(
  blackouts: readonly T[],
  date: string,
): T | null {
  return blackouts.find((one) => one.fromDate <= date && date <= one.toDate) ?? null;
}

/**
 * ÅRETS PROVISJON (vedtak 102.1): satsen som gjelder og hva den ble av årets godtatte bud. Regnet i
 * basen (`app.artist_commission_year`), for byrået og for den som har hele artisten med økonomi (292.1).
 */
export const artistCommissionSchema = z
  .object({
    /** Året summen gjelder. Kalenderåret, fordi et byrå gjør opp per år og ikke per festivalårgang. */
    year: z.number().int(),
    rateBp: z.number().int().min(0).max(10_000).nullable(),
    fromArtist: z.boolean(),
    /** Honorarene på de godtatte budene i år. Honoraret ALENE — ikke reise og opphold. */
    feeMinor: z.number().int().nonnegative(),
    commissionMinor: z.number().int().nonnegative(),
    currency: z.string(),
    /** Hvor mange bookinger tallet er summen av. Én uten kontekst er ikke en opplysning. */
    bookings: z.number().int().nonnegative(),
  })
  .meta({ id: 'ArtistCommission' });
export type ArtistCommission = z.infer<typeof artistCommissionSchema>;
